import React, { useContext } from 'react';
import classNames from 'classnames';

import data from '../../data.yaml';
import { RootContext } from '../context';
import { iconCircle, iconPointsRequirement, perkAvailable, perkIsTiered } from '../utils';
import { PerkSummary } from './PerkSummary';
import { Widget } from './Widget';

import styles from './SelectionPanel.module.css';

export const SelectionPanel: React.FC = () => {
  const { perks, pointsLeft, selectedPerk, setPerk, vocation } = useContext(RootContext);

  if (selectedPerk === null) {
    return <>
      <Widget className={styles.panel}>
        <span>Selection</span>
        <p className={styles.empty}>Select a slice on the wheel to manage its promotion points.</p>
      </Widget>
      <Widget className={styles.panel}>
        <span>Information</span>
        <p className={styles.empty}>Select a slice to see its Dedication and Conviction perks.</p>
      </Widget>
    </>;
  }

  const circle = iconCircle(selectedPerk);
  const maxPoints = data.pointsPerCircle[circle];
  const currentPoints = perks[selectedPerk];
  const convictionIndex = data.perks.conviction[vocation][selectedPerk];
  const dedicationIndex = data.perks.dedication[selectedPerk];
  const available = perkAvailable(perks, selectedPerk);
  const progress = Math.round(currentPoints / maxPoints * 100);
  const convictionLevel = Object.entries(perks).filter(([ key, points ]) => {
    return points === data.pointsPerCircle[iconCircle(+key)] &&
      data.perks.conviction[vocation][+key] === convictionIndex;
  }).length;

  const changeBy = (value: number) => () => setPerk(selectedPerk, currentPoints + value);

  return <>
    <Widget className={styles.panel}>
      <span>Selection</span>
      <div className={styles.selectionHeader}>
        <strong>Slice {selectedPerk + 1}</strong>
        <span className={classNames(styles.state, !available && styles.locked)}>
          {available ? `${currentPoints} / ${maxPoints}` : 'Locked'}
        </span>
      </div>
      <div className={styles.progress} aria-label={`${progress}% filled`}>
        <span style={{ width: `${progress}%` }}/>
      </div>
      {!available && <small>Requires a completed adjacent slice after {iconPointsRequirement(selectedPerk)} points.</small>}
      <div className={styles.controls}>
        <button onClick={changeBy(-250)}>-max</button>
        <button onClick={changeBy(-10)}>-10</button>
        <button onClick={changeBy(-1)}>-1</button>
        <button onClick={changeBy(1)} disabled={pointsLeft === 0}>+1</button>
        <button onClick={changeBy(10)} disabled={pointsLeft === 0}>+10</button>
        <button onClick={changeBy(250)} disabled={pointsLeft === 0}>+max</button>
      </div>
    </Widget>

    <Widget className={styles.panel}>
      <span>Information</span>
      <div className={styles.infoBlock}>
        <small>Conviction Perk</small>
        <PerkSummary
          as="article"
          type="conviction"
          index={convictionIndex}
          value={perkIsTiered(data.conviction[convictionIndex]) ? convictionLevel : 1}/>
      </div>
      <div className={styles.infoBlock}>
        <small>Dedication Perk · per promotion point</small>
        <PerkSummary
          as="article"
          type="dedication"
          vocation={vocation}
          index={dedicationIndex}
          value={1}/>
      </div>
    </Widget>
  </>;
};
